import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useLocales } from '@/hooks/use-locales'
import {
  getAdminDictionary,
  getLocalDictionary,
  resolveAdminLocale,
  setAdminLocale,
  translate,
  type Dictionary,
} from '@/lib/i18n/get-dictionary'

type AdminI18nContextValue = {
  locale: string
  dictionary: Dictionary
  setLocale: (code: string) => void
  t: (key: string, params?: Record<string, string | number>) => string
  isLoading: boolean
}

const AdminI18nContext = createContext<AdminI18nContextValue | null>(null)

export function AdminI18nProvider({ children }: { children: ReactNode }) {
  const localesQ = useLocales()
  const supported = useMemo(
    () => (localesQ.data ?? []).map((l) => l.code),
    [localesQ.data],
  )
  const qc = useQueryClient()

  const [locale, setLocaleState] = useState(() =>
    resolveAdminLocale(supported.length ? supported : ['tr', 'en']),
  )

  useEffect(() => {
    if (supported.length === 0) return
    const next = resolveAdminLocale(supported)
    setLocaleState((prev) => (supported.includes(prev) ? prev : next))
  }, [supported])

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const dictQ = useQuery({
    queryKey: ['admin-ui-dictionary', locale],
    queryFn: () => getAdminDictionary(locale),
    staleTime: 60_000,
    placeholderData: () => getLocalDictionary(locale),
  })

  const dictionary = dictQ.data ?? getLocalDictionary(locale)

  const setLocale = useCallback(
    (code: string) => {
      const next = supported.includes(code) ? code : resolveAdminLocale(supported)
      setAdminLocale(next)
      setLocaleState(next)
      void qc.invalidateQueries({ queryKey: ['admin-ui-dictionary'] })
    },
    [qc, supported],
  )

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) =>
      translate(dictionary, key, params),
    [dictionary],
  )

  const value = useMemo(
    () => ({
      locale,
      dictionary,
      setLocale,
      t,
      isLoading: dictQ.isLoading,
    }),
    [locale, dictionary, setLocale, t, dictQ.isLoading],
  )

  return <AdminI18nContext.Provider value={value}>{children}</AdminI18nContext.Provider>
}

export function useAdminI18n(): AdminI18nContextValue {
  const ctx = useContext(AdminI18nContext)
  if (!ctx) {
    throw new Error('useAdminI18n must be used within AdminI18nProvider')
  }
  return ctx
}

export function useT() {
  return useAdminI18n().t
}
